import { BridgeComponent } from '@hotwired/hotwire-native-bridge'
import { csrfToken, refreshCsrfToken } from '../../utils/csrf'

// Asks the app for the store review window (iOS AppStore.requestReview, Android In-App Review).
//
// Placed on <body> only when the server says the account is ready (score, quotas: see
// User::Reviewable) and the shell has the `review` component. This controller only picks
// the moment, as the stores forbid asking at launch or in the middle of a task:
//   - not in a session opened from a push: a page carrying `?notification=` starts one;
//   - at least 45 s since the session's first page, and at least 2 pages seen;
//   - on a calm screen (a list, a profile: `content_for :review_calm_screen`), shown for at
//     least 2 s, so the window never pops up while the screen is still appearing.
//
// The app answers `{ requested }`: false when this page is not the one on screen (a form
// opened over it, another tab), and only then is the request recorded (POST /review_prompts).
//
// The wait stops when the app goes to the background (or the tab is left): coming back must not
// fire it on the spot, as asking at launch is forbidden. The next page starts it again. Turbo
// previews (a cached page shown while the real one loads) are not counted as pages.
//
// "Session" is this webview's sessionStorage (each tab and each modal has its own, and coming
// back from the background reloads nothing): 30 minutes without a page start a new one. A
// request made by any webview silences the others through localStorage, which they share.
// Rules and trade-offs in doc/notation-stores.md.
const SESSION_KEY = 'review-session'
const REQUESTED_KEY = 'review-requested-at'
const MIN_DURATION = 45_000
const MIN_PAGES = 2
const MIN_DWELL = 2_000
const IDLE_RESTART = 30 * 60_000
const MIN_INTERVAL = 60 * 24 * 3600_000 // User::Reviewable::MIN_INTERVAL

export default class extends BridgeComponent {
  static component = 'review'
  static values = { calm: Boolean }

  connect() {
    super.connect()
    if (document.documentElement.hasAttribute('data-turbo-preview')) return

    const session = this.#trackPage()
    if (session.fromPush || session.pages < MIN_PAGES || !this.calmValue) return

    const wait = Math.max(MIN_DWELL, session.startedAt + MIN_DURATION - Date.now())
    this.timeout = setTimeout(() => this.#request(), wait)
    document.addEventListener('visibilitychange', this.#stopWhenHidden)
  }

  disconnect() {
    this.#stop()
    super.disconnect()
  }

  #stopWhenHidden = () => {
    if (document.visibilityState === 'hidden') this.#stop()
  }

  #stop() {
    clearTimeout(this.timeout)
    document.removeEventListener('visibilitychange', this.#stopWhenHidden)
  }

  #request() {
    document.removeEventListener('visibilitychange', this.#stopWhenHidden)
    if (this.#recentlyRequested()) return

    this.send('request', {}, ({ data }) => {
      if (data?.requested !== true) return

      this.#store(() => localStorage.setItem(REQUESTED_KEY, Date.now()))
      this.#record().catch(() => {})
    })
  }

  // The window is already open: the request must not be lost to a page whose CSRF token went
  // stale (session rotated by another tab). One retry with a fresh token, as in push_controller;
  // the server's other answers (201, 204) are never 422.
  async #record() {
    const response = await this.#post()
    if (response.status !== 422 || !(await refreshCsrfToken())) return

    await this.#post()
  }

  #post() {
    return fetch('/review_prompts', { method: 'POST', headers: { 'X-CSRF-Token': csrfToken() } })
  }

  #recentlyRequested() {
    const at = Number(this.#store(() => localStorage.getItem(REQUESTED_KEY)))
    return Date.now() - at < MIN_INTERVAL
  }

  // Storage unavailable: the page then simply never asks.
  #trackPage() {
    const now = Date.now()
    const fromPush = new URLSearchParams(window.location.search).has('notification')
    let session = this.#store(() => JSON.parse(sessionStorage.getItem(SESSION_KEY)))
    if (session === undefined) return { fromPush: true }

    if (!session || fromPush || now - session.lastPageAt > IDLE_RESTART) {
      session = { startedAt: now, pages: 0, fromPush }
    }
    session.pages += 1
    session.lastPageAt = now
    this.#store(() => sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)))
    return session
  }

  // undefined when the storage throws (private mode, blocked site data).
  #store(operation) {
    try {
      return operation()
    } catch {
      return undefined
    }
  }
}
